import { message } from 'antd';
import { matchRoutes } from 'umi';
import type { RequestConfig } from 'umi';

import { ErrorMsgMap } from './errorMap';
import { UserPermission } from '@/types';
import { UserInfo } from './apis/user';
import { getAccessToken, removeAccessToken } from './utils/sessStorage';
import { getCurrentFullPath } from './utils/feishu';

interface RouteObject {
  caseSensitive?: boolean;
  children?: RouteObject[];
  element?: React.ReactNode;
  index?: boolean;
  path?: string;
  title?: string;
}

message.config({
  maxCount: 1, // 设置同时显示的最大提示信息数量
});

interface InitialState {
  user: UserInfo;
  isSuperAdmin: boolean;
  userPermission: UserPermission[];
}

// 全局初始化数据配置，用于 Layout 用户信息和权限初始化
// 更多信息见文档：https://umijs.org/docs/api/runtime-config#getinitialstate
export async function getInitialState() {
  const initialState: InitialState = {
    user: {
      name: '',
      ID: '',
      fullName: '',
      employeeNo: '',
      departmentRef: [],
    },
    isSuperAdmin: false,
    userPermission: [],
  }

  return initialState;
}

// 是否已经检验过
// https://umijs.org/docs/max/request#运行时配置
export const request: RequestConfig = {
  baseURL: process.env.BASEURL ?? '/v1',
  // other axios options you want
  errorConfig: {
    // 错误接收及处理
    async errorHandler(error: any, opts: any) {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.response) {
        const pathname = location.pathname;
        const fullPath = getCurrentFullPath();
        switch (error.response.status) {
          case 401:
            removeAccessToken();
            if (!['/login', '/auth/callback'].includes(pathname)) {
              // 401 未登录，跳转到登录页
              window.location.href = `/login?from=${encodeURIComponent(fullPath)}`;
            }

            return;
          case 403:
            message.error('无权限');
            return;
        }

        if (error?.response.data.code) {
          const data = error?.response.data;
          if (data.code) {
            const msg = ErrorMsgMap[`${data.reason as string}`];
            if (msg) return message.error(msg);
          }
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },
  requestInterceptors: [
    (config: any) => {
      config.headers.authorization = `Bearer ${getAccessToken()}`;
      return config;
    },
  ],
  responseInterceptors: [
    // 直接写一个 function，作为拦截器
    (response: any) => {
      // 不再需要异步处理读取返回体内容，可直接在data中读出，部分字段可在 config 中找到
      // const { data = {} as any, config } = response;
      // do something
      return response;
    },
    // 一个二元组，第一个元素是 request 拦截器，第二个元素是错误处理
    [
      (response: any) => {
        return response;
      },
      (error: any) => {
        return Promise.reject(error);
      },
    ],
  ],
};

export function onRouteChange({
  clientRoutes,
  location,
}: {
  clientRoutes: RouteObject[];
  location: Location;
}) {
  const route = matchRoutes(clientRoutes, location.pathname)?.pop()?.route;

  if (route) {
    document.title = (route as RouteObject).title || '思谋资产管理系统';
  }
}
